import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { formatCurrency } from '../../utils/helpers';
import dashboardService from '../../services/dashboardService';
import { InvoiceItemSearchResult } from '../../types/dashboardTypes';
import instructionService, { Instruction } from '../../services/instructionService';
import InstructionMarquee from '../../components/InstructionMarquee';
import styles from './Dashboard.module.scss';
import { useNavigate } from 'react-router-dom';
import { formatDateDMY } from '../../utils/helpers';
import { toast, ToastContainer } from 'react-toastify';


interface DashboardStats {
  monthlyRevenue: number;
  pendingAmount: number;
  receivedAmount: number;
  totalCustomers: number;
  totalInvoices: number;
  overdueInvoices: number;
  extraAmount: number;
}

interface ReceivedAmountData {
  name: string;
  value: number;
  color: string;
}

// DashboardIcon component with SVG icons
const DashboardIcon: React.FC<{ iconName: string; size?: number; className?: string }> = ({ 
  iconName, 
  size = 24, 
  className = "" 
}) => {
  const getIcon = (name: string) => {
    switch (name) {
      case 'revenue':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M12 2v20m5-5l-5-5-5 5m5-5v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15 8h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
        
      case 'pending':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
        
      case 'received':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M9 11l3 3 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.5 0 2.91.37 4.15 1.02" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
        
      case 'customers':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
        
      case 'invoices':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
        
      // case 'overdue':
      //   return (
      //     <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      //       <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      //     </svg>
      //   );
        
      case 'create-invoice':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M9 14h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
        
      case 'record-payment':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 10h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
        
      case 'add-customer':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="8.5" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="23" y1="11" x2="17" y2="11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
        
      case 'reports':
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <path d="M3 3v8h8V3H3zM13 3v8h8V3h-8zM3 13v8h8v-8H3zM13 13v8h8v-8h-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
        
      default:
        return (
          <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        );
    }
  };

  return getIcon(iconName);
};

const Dashboard: React.FC = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [receivedAmount, setReceivedAmount] = useState<ReceivedAmountData[]>([]);
  const [loading, setLoading] = useState(true);
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const customerLoginUrl = `${window.location.origin}/customer-login`;
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemSearchResults, setItemSearchResults] = useState<InvoiceItemSearchResult[]>([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [itemSearched, setItemSearched] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied!"); // or alert("Copied!")
    }).catch((err) => {
      console.error("Failed to copy: ", err);
    });
  };

  // Mock data for development
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const today = new Date().toLocaleDateString('en-CA') // 'YYYY-MM-DD'
        // Pass the date to the service
        const dashboardData = await dashboardService.getDashboardData(today);
        setStats(dashboardData);

        // Fetch received amount by payment method
        const receivedAmountData = await dashboardService.getReceivedAmount(today);
        console.log("receivedAmountData:::::::", receivedAmountData)
        setReceivedAmount(receivedAmountData);

        // Fetch instructions
        const fetchedInstructions = await instructionService.getActive();
        setInstructions(fetchedInstructions || []); // Ensure we always set an array

        // (Optional) Fetch recent invoices/payments if you want to display them
        // const recentInvoices = await dashboardService.getRecentInvoices();
        // const recentPayments = await dashboardService.getRecentPayments();
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Debounced search for invoice items by description
  useEffect(() => {
    const query = itemSearchQuery.trim();
    if (!query) {
      setItemSearchResults([]);
      setItemSearched(false);
      return;
    }

    setItemSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await dashboardService.searchInvoiceItems(query);
        setItemSearchResults(results || []);
      } catch (error) {
        console.error('Failed to search invoice items:', error);
        setItemSearchResults([]);
      } finally {
        setItemSearchLoading(false);
        setItemSearched(true);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [itemSearchQuery]);

  const today = new Date();
  const dayName = today.toLocaleDateString('en-CA', { weekday: 'long' });
  const dayWithDate = `${dayName}, ${formatDateDMY(today)}`;

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: string;
    color: string;
    onClick?: () => void;
    clickable?: boolean;
  }> = ({ title, value, subtitle, icon, color, onClick, clickable }) => (
    <div
      className={styles.statCard + (clickable ? ' ' + styles.clickable : '')}
      style={{ cursor: clickable ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <div className={styles.statIcon} style={{ backgroundColor: color }}>
        <DashboardIcon iconName={icon} size={24} />
      </div>
      <div className={styles.statContent}>
        <h3 className={styles.statTitle}>{title}</h3>
        <p className={styles.statValue}>{value}</p>
        {subtitle && <p className={styles.statSubtitle}>{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  const totalReceived = receivedAmount.reduce((sum, item) => sum + Number(item.value), 0);
  const paymentMethodRows = receivedAmount.map(item => ({
    ...item,
    percent: totalReceived ? ((Number(item.value) / totalReceived) * 100).toFixed(2) : '0.00'
  }));

  const CopyIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M16 1H4C2.89543 1 2 1.89543 2 3V17H4V3H16V1Z"
        fill="currentColor"
      />
      <path
        d="M20 5H8C6.89543 5 6 5.89543 6 7V21C6 22.1046 6.89543 23 8 23H20C21.1046 23 22 22.1046 22 21V7C22 5.89543 21.1046 5 20 5ZM20 21H8V7H20V21Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className={styles.dashboard}>
      <div className={styles.fullHeader}>
  <div className={styles.headerLeft}>
    <h1>Dashboard</h1>
    <p>Welcome back, {auth.user?.name}! ({dayWithDate})</p>
  </div>
  
  <div className={styles.headerRight}>
  <a
    href={customerLoginUrl}
    target="_blank"
    rel="noopener noreferrer"
    className={styles.urlLink}
  >
    {customerLoginUrl}
  </a>
  <button
    onClick={() => copyToClipboard(customerLoginUrl)}
    className={styles.copyButton}
    title="Copy Dashboard URL"
  >
    <CopyIcon size={16} />
  </button>
</div>

</div>

      

      {/* Statistics Cards */}
      <div className={styles.statsGrid}>
        <StatCard
          title="Invoice Amount"
          value={formatCurrency(stats?.monthlyRevenue || 0)}
          subtitle={`Today's invoice amount \n(Today total invoice - ${stats?.totalInvoices || 0})`}
          icon="revenue"
          color="#3B82F6"
          onClick={() => navigate('/invoices')}
          clickable
        />
        <StatCard
          title="Pending Amount"
          value={formatCurrency(stats?.pendingAmount || 0)}
          subtitle="Outstanding invoices"
          icon="pending"
          color="#F59E0B"
          onClick={() => navigate('/dashboard/pending-amount')}
          clickable
        />
        <StatCard
          title="Received Amount"
          value={formatCurrency(stats?.receivedAmount || 0)}
          subtitle="Today's received amount"
          icon="received"
          color="#10B981"
          onClick={() => navigate('/received-amount')}
          clickable
        />
        <StatCard
          title="Total Customers"
          value={stats?.totalCustomers || 0}
          subtitle="Active customers"
          icon="customers"
          color="#8B5CF6"
          onClick={() => navigate('/reports')}
          clickable
        />
        {/* <StatCard
          title="Total Invoices"
          value={(stats?.totalInvoices || 0)}
          subtitle="Today's invoices"
          icon="invoices"
          color="#06B6D4"
          onClick={() => navigate('/invoices')}
          clickable
        /> */}

        <StatCard
          title="Extra Amount"
          value={formatCurrency(Math.abs(stats?.extraAmount || 0))}
          subtitle="Total extra amount"
          icon="extra"
          color="#EF4444"
          onClick={() => navigate('/dashboard/extra-amount')}
          clickable
        />
      </div>

      {/* <StatCard
          title="Overdue Invoices"
          value={stats?.overdueInvoices || 0}
          subtitle="Requires attention"
          icon="overdue"
          color="#EF4444"
          onClick={() => navigate('/dashboard/overdue-invoices')}
          clickable
        /> */}

      {/* Charts Section */}
      <div className={styles.chartsSection}>
        <div className={styles.chartGrid}>
          <div className={styles.instructionSection}>
            <InstructionMarquee instructions={instructions} />
          </div>
          <div className={styles.chartContainers}>
            <h4 style={{marginBottom: 10, fontWeight: 800, color: '#1f2937'}}>Payment Methods</h4>
            <table className={styles.paymentMethodTable}>
              <thead>
                <tr>
                  <th>Payment Method</th>
                  <th>Amount (₹)</th>
                  {/* <th>Percent (%)</th> */}
                </tr>
              </thead>
              <tbody>
                {paymentMethodRows.map((row, idx) => (
                  <tr key={row.name || idx}>
                    <td>{row.name?.toUpperCase()}</td>
                    <td>{formatCurrency(Number(row.value))}</td>
                    {/* <td>{row.percent}%</td> */}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Total</td>
                  <td style={{ fontWeight: 'bold' }}>{formatCurrency(totalReceived)}</td>
                  {/* <td></td> */}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {/* <div className={styles.quickActions}>
        <h2>Quick Actions</h2>
        <div className={styles.actionButtons}>
          <button className={styles.actionButton}>
            <span className={styles.actionIcon}>
              <DashboardIcon iconName="create-invoice" size={20} />
            </span>
            <span>Create Invoice</span>
          </button>
          <button className={styles.actionButton}>
            <span className={styles.actionIcon}>
              <DashboardIcon iconName="record-payment" size={20} />
            </span>
            <span>Record Payment</span>
          </button>
          <button className={styles.actionButton}>
            <span className={styles.actionIcon}>
              <DashboardIcon iconName="add-customer" size={20} />
            </span>
            <span>Add Customer</span>
          </button>
          <button className={styles.actionButton}>
            <span className={styles.actionIcon}>
              <DashboardIcon iconName="reports" size={20} />
            </span>
            <span>View Reports</span>
          </button>
        </div>
      </div> */}

      {/* Invoice Item Description Search */}
      <div className={styles.chartsSection}>
        <h4 style={{ marginBottom: 10, fontWeight: 800, color: '#1f2937' }}>Search Invoice Items</h4>
        <input
          type="text"
          value={itemSearchQuery}
          onChange={e => setItemSearchQuery(e.target.value)}
          placeholder="Search by item description..."
          style={{
            width: '100%',
            maxWidth: 400,
            padding: '8px 12px',
            marginBottom: 12,
            border: '1px solid #d1d5db',
            borderRadius: 6
          }}
        />

        {itemSearchLoading ? (
          <p>Searching...</p>
        ) : itemSearchQuery.trim() && itemSearched && itemSearchResults.length === 0 ? (
          <p>No matching invoice items found.</p>
        ) : itemSearchResults.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.paymentMethodTable}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Customer Name</th>
                  <th>Invoice Number</th>
                  <th>Date</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {itemSearchResults.map(row => (
                  <tr key={row.item_id}>
                    <td>{row.description}</td>
                    <td>{row.customer_name || '-'}</td>
                    <td>
                      <span
                        onClick={() => navigate(`/invoices/${row.invoice_id}`)}
                        style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {row.invoice_number}
                      </span>
                    </td>
                    <td>{row.invoice_date ? formatDateDMY(row.invoice_date) : '-'}</td>
                    <td>{row.created_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Dashboard;
